import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { configHelper } from '@ilteducation/config';
import { useUserContext } from '@ilteducation/ilt-auth';
import axios from 'axios';
import { ExerciseWithProgress, useExerciseStore } from '@/store/exercises';
import shuffle from 'lodash/shuffle';
import { ExerciseAnswers, Submission } from '@ilteducation/submission-types';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { trackQuizResults } from '@/composables/use-quiz-analytics';

const submissionApi = configHelper.getApiUrl('submissions');

const useQuizStore = defineStore('quiz', () => {
  const exerciseStore = useExerciseStore();
  const currentQuiz = ref<ExerciseWithProgress>();
  const loadingText = ref('quiz.loading_quiz');
  const isLoading = ref(false);
  const isError = ref(false);
  const currentQuestion = ref(1);
  const answers = ref<any[]>([]);
  const correctAnswers = ref<any[]>([]);
  const submission = ref<Submission>();
  const mode = ref<'quiz' | 'quiz-results'>('quiz');

  function shuffleQuizOptions() {
    if (!currentQuiz.value) {
      return;
    }
    currentQuiz.value.content = (currentQuiz.value.content as CloseEndedQuestion[]).map((question) => {
      if (question.options) {
        return {
          ...question,
          options: shuffle(question.options),
        };
      }
      return question;
    });
  }
  function reset() {
    isError.value = false;
    currentQuestion.value = 1;
    answers.value = [];
    correctAnswers.value = [];
    mode.value = 'quiz';
    submission.value = undefined;
    shuffleQuizOptions();
  }

  async function nextQuestion() {
    if (!currentQuiz.value) {
      return;
    }
    isLoading.value = true;
    const lastQuestion = currentQuestion.value === currentQuiz.value.content.length;
    const submissionData: Partial<Submission> = {};
    const mappedAnswers: ExerciseAnswers = {};

    answers.value.forEach((answer) => {
      const question = currentQuiz.value!.content[answer.questionId] as CloseEndedQuestion;
      mappedAnswers[question.id || ''] = {
        optionId: answer.optionId,
        type: 'ClosedEndedQuestionAnswer',
      };
    });

    submissionData.answers = {
      [currentQuiz.value.id]: mappedAnswers,
    };
    submissionData.status = lastQuestion ? 'Submitted' : 'InProgress';

    try {
      if (submission.value) {
        await updateSubmission(submissionData);
      }

      if (currentQuestion.value === 1 && !submission.value) {
        await createSubmission();
        submission.value!.answers = submissionData.answers;
      }
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (error) {
      isError.value = true;
    } finally {
      isLoading.value = false;
    }

    // setting the next question
    if (currentQuestion.value + 1 > currentQuiz.value.content.length) {
      return;
    }
    currentQuestion.value += 1;
  }
  function prevQuestion() {
    if (currentQuestion.value === 1) {
      return;
    }
    currentQuestion.value -= 1;
  }
  function answerQuestion(optionId: string) {
    const exAnswer = answers.value.find((answer) => answer.questionId === currentQuestion.value - 1);

    if (exAnswer) {
      exAnswer.optionId = optionId;
      return;
    }

    answers.value.push({ questionId: currentQuestion.value - 1, optionId });
  }

  async function createSubmission() {
    if (!currentQuiz.value) {
      return Promise.resolve();
    }
    const userContext = useUserContext();
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { progress, ...quiz } = currentQuiz.value;
    return axios
      .post(`${submissionApi}/`, {
        contextId: currentQuiz.value.id,
        context: {
          type: 'Quiz',
          exercise: quiz,
          realm: userContext.realm,
        },
      })
      .then((response) => {
        submission.value = response.data as Submission;
      });
  }
  async function updateSubmission(data: Partial<Submission>) {
    return axios.patch(`${submissionApi}/${submission.value?.id}`, data).then(() => {
      submission.value = { ...submission.value, answers: data.answers } as Submission;
    });
  }
  function setGradingStatus() {
    loadingText.value = 'quiz.correcting_answers';
    isLoading.value = true;
  }

  function setCorrectAnswers() {
    if (!currentQuiz.value) {
      return;
    }
    isLoading.value = false;
    correctAnswers.value = [];
    for (let index = 0; index < currentQuiz.value.content.length; ++index) {
      const questions = currentQuiz.value.content as CloseEndedQuestion[];
      const currentAnswer = answers.value.find((answer) => answer.questionId === index);

      correctAnswers.value.push({
        questionId: index,
        optionId: currentAnswer.optionId,
        correctOptionId: questions[index].answer,
        correct: questions[index].answer === currentAnswer?.optionId,
      });
    }

    trackQuizResults(currentQuiz.value, correctAnswers.value);

    loadingText.value = 'quiz.loading_quiz';
  }

  async function compareAnswers() {
    if (!currentQuiz.value) {
      return;
    }
    await nextQuestion();

    setCorrectAnswers();
    if (correctAnswers.value.filter((answer) => answer.correct).length === currentQuiz.value.content.length) {
      // store the progress locally to immediately unlock the next quiz
      exerciseStore.setProgress(currentQuiz.value.resourceId, currentQuiz.value.id);
    }
    mode.value = 'quiz-results';
  }

  return {
    mode,
    loadingText,
    isLoading,
    isError,
    currentQuiz,
    currentQuestion,
    answers,
    correctAnswers,
    submission,
    reset,
    nextQuestion,
    prevQuestion,
    answerQuestion,
    setGradingStatus,
    compareAnswers,
    questionHasAnswer: computed(
      () => !!answers.value.find((answer) => answer.questionId === currentQuestion.value - 1),
    ),
  };
});

export default useQuizStore;
